import { useEffect, useRef, useState } from "react";
import { getToken, type Me, type ServerConfig } from "../lib/api.js";
import { Drawer, Notice } from "../components/ui.js";
import { RemoteSession } from "rustdesk-protocol/session.js";
import { VideoSink, detectCodecs, type CodecName } from "rustdesk-protocol/video.js";
import { PreferCodec, type PeerInfo } from "rustdesk-protocol/message.js";
import { savedHash } from "../lib/credentials.js";

/**
 * The machine's camera.
 *
 * RustDesk treats a camera view as its own connection type: the peer sends
 * camera frames in place of screen frames, over the same video path, so this
 * is a second session with `view_camera` set rather than a mode of the screen
 * session. A machine with several cameras lists them where a screen session
 * would list displays.
 */
export default function CameraView({
  peerId,
  me,
  serverKeyProvider,
  password,
  onClose,
}: {
  peerId: string;
  me: Me;
  serverKeyProvider: () => Promise<ServerConfig>;
  password?: string;
  onClose: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sessionRef = useRef<RemoteSession | null>(null);
  const sinkRef = useRef<VideoSink | null>(null);

  const [status, setStatus] = useState("Connecting…");
  const [error, setError] = useState("");
  const [info, setInfo] = useState<PeerInfo | null>(null);
  const [camera, setCamera] = useState(0);
  const cameraRef = useRef(0);
  cameraRef.current = camera;

  useEffect(() => {
    let cancelled = false;
    const canvas = canvasRef.current;
    if (!canvas) return;

    (async () => {
      try {
        const cfg = await serverKeyProvider();
        const codecs = await detectCodecs();
        const sink = new VideoSink(canvas, {
          onError: (m) => setError(m),
          onNeedRefresh: () => sessionRef.current?.refreshVideo(),
        });
        sinkRef.current = sink;

        // Its own connection, so its own credentials: the screen session may
        // have been let in by someone at the machine, leaving no password here
        // to reuse.
        const hash = password ? null : await savedHash(peerId);

        const session = new RemoteSession(
          {
            origin: window.location.origin,
            token: getToken(),
            peerId,
            serverKey: cfg.key,
            password,
            passwordHash: hash ?? undefined,
            connType: "view_camera",
            myName: me.display_name || me.name,
            myId: "web",
            options: {
              supportedDecoding: {
                vp8: codecs.vp8, vp9: codecs.vp9, h264: codecs.h264,
                h265: codecs.h265, av1: codecs.av1,
                prefer: codecs.vp9 ? PreferCodec.VP9
                  : codecs.h264 ? PreferCodec.H264 : PreferCodec.Auto,
              },
            },
          },
          {
            onState: (s) => {
              if (s === "connected") setStatus("Connected");
              else if (s === "authenticating") {
                setStatus(password || hash
                  ? "Signing in"
                  : "Waiting for someone at the machine to accept");
              }
            },
            onPasswordRequired: (wrong) =>
              setError(wrong
                ? "The saved password for this machine was not accepted."
                : "This machine needs a password. Reconnect the session and enter it."),
            onError: (m) => setError(m),
            onClose: (r) => setStatus(r || "Disconnected"),
            onPeerInfo: (i) => {
              if (cancelled) return;
              setInfo(i);
              setCamera(i.currentDisplay);
              setStatus("Connected");
              sessionRef.current?.refreshVideo();
            },
            onMessage: (msg) => {
              if (msg.kind !== "video_frame" || !msg.frames.length) return;
              if (msg.display !== cameraRef.current) return;
              const codec = msg.codec as CodecName;
              if (!sink.ready(codec)) {
                void sink.configure(codec);
              }
              for (const f of msg.frames) sink.decode(f.data, f.key, f.pts);
            },
          },
        );
        sessionRef.current = session;
        await session.connect();
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();

    return () => {
      cancelled = true;
      sessionRef.current?.close("closed by user");
      sinkRef.current?.close();
      sessionRef.current = null;
      sinkRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peerId]);

  const cameras = info?.displays ?? [];

  return (
    <Drawer
      title="Camera"
      onClose={onClose}
      footer={<button className="btn" onClick={onClose}>Close</button>}
    >
      <div className="stack">
        {error ? <Notice>{error}</Notice> : null}
        <div className="row row--between">
          <span className="muted small">{status}</span>
          {cameras.length > 1 ? (
            <select
              value={camera}
              style={{ width: "auto" }}
              aria-label="Camera"
              onChange={(e) => {
                const i = Number(e.target.value);
                setCamera(i);
                cameraRef.current = i;
                sessionRef.current?.switchDisplay(i);
              }}
            >
              {cameras.map((c, i) => (
                <option key={i} value={i}>Camera {i + 1} ({c.width}&times;{c.height})</option>
              ))}
            </select>
          ) : null}
        </div>
        <canvas
          ref={canvasRef}
          style={{ width: "100%", background: "#000", borderRadius: "var(--radius)" }}
        />
        {!cameras.length && !error ? (
          <div className="notice">
            If this machine has no camera, or RustDesk is not allowed to use it, no
            picture will appear.
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}
